package org.moxultimate.topwithmox.tech.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.tech.data.TechResponse
import org.moxultimate.topwithmox.tech.data.TechTagResponse
import org.moxultimate.topwithmox.tech.data.TechTypeResponse

/**
 * TechRepository
 * Handles all read operations for tech/how-to entries, tags, and types.
 * Write operations (create, update, delete) live in TechCRUDRepository.
 *
 * @param httpClient Ktor HttpClient injected via Koin.
 */
class TechRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/technology"
    private val tagUrl  = "$baseAPI/tech-tag"
    private val typeUrl = "$baseAPI/tech-type"

    /**
     * getTechs
     * Fetches a page of tech entries using cursor-based pagination (20 items, newest first).
     * Pass [beforeId] = Long.MAX_VALUE to load the first page.
     *
     * @param beforeId Pagination cursor — loads items with id strictly less than this value.
     * @return Flow emitting Loading → Success(List<TechResponse>) or Error.
     */
    fun getTechs(beforeId: Long = Long.MAX_VALUE): Flow<APIState<List<TechResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get(baseUrl) {
                parameter("beforeId", beforeId)
            }
        })
    }

    /**
     * getTechById
     * Fetches a single tech entry via GET /technology/{id}.
     *
     * @param id Tech entry ID to fetch.
     * @return Flow emitting Loading → Success(TechResponse) or Error.
     */
    fun getTechById(id: Long): Flow<APIState<TechResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.get("$baseUrl/$id") })
    }

    /**
     * getAllTags
     * Fetches all tech tags from GET /tech-tag. Used for search autocomplete.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTags(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<TechTagResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(tagUrl) },
                transformer = { entities -> entities.mapNotNull { it.tag }.distinct() },
            )
        )
    }

    /**
     * getAllTypes
     * Fetches all tech types from GET /tech-type. Used to populate the type filter chips.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTypes(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<TechTypeResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(typeUrl) },
                transformer = { entities -> entities.mapNotNull { it.type }.distinct() },
            )
        )
    }
}
